/**
 * Loft Geometry
 *
 * Real ruled loft between two parallel (Z-separated) closed profiles:
 * arc-length resampled loops with start-point alignment (no twist), a quad
 * side band and triangulated caps — a closed solid, not a schematic box.
 * Shared by FormGenerator.createBlend and SolidKernel.createBlend.
 */

import * as THREE from 'three';

/** Resample a closed shape contour into `segments` equally-spaced points. */
function resampleClosed(shape: THREE.Shape, segments: number): THREE.Vector2[] {
    const spaced = shape.getSpacedPoints(segments);
    // getSpacedPoints returns segments+1 points with last == first for
    // closed shapes; drop the duplicate.
    if (spaced.length > 1) spaced.pop();
    return spaced;
}

/**
 * Rotate the loop array so it starts at the point nearest `reference` —
 * keeps corresponding vertices paired and the loft untwisted.
 */
function alignLoopStart(loop: THREE.Vector2[], reference: THREE.Vector2): THREE.Vector2[] {
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < loop.length; i++) {
        const d = loop[i].distanceToSquared(reference);
        if (d < bestDist) {
            bestDist = d;
            best = i;
        }
    }
    return loop.slice(best).concat(loop.slice(0, best));
}

/**
 * Build the loft solid between `bottom` (z=0) and `top` (z=height).
 * Outer contours are lofted; profile holes are not carried through the
 * ruled band (declared limitation — blends in BIM are solid transitions).
 */
export function createLoftGeometry(
    bottom: THREE.Shape,
    top: THREE.Shape,
    height: number,
    segments = 24
): THREE.BufferGeometry {
    const bottomLoop = resampleClosed(bottom, segments);
    const topLoop = alignLoopStart(resampleClosed(top, segments), bottomLoop[0]);

    const positions: number[] = [];

    // Side band: outward quads along the CCW bottom contour.
    const n = Math.min(bottomLoop.length, topLoop.length);
    for (let i = 0; i < n; i++) {
        const iNext = (i + 1) % n;
        const b0 = bottomLoop[i];
        const b1 = bottomLoop[iNext];
        const t0 = topLoop[i];
        const t1 = topLoop[iNext];
        // Tri 1: b0, b1, t1 — Tri 2: b0, t1, t0 (outward winding).
        positions.push(b0.x, b0.y, 0, b1.x, b1.y, 0, t1.x, t1.y, height);
        positions.push(b0.x, b0.y, 0, t1.x, t1.y, height, t0.x, t0.y, height);
    }

    // Caps: triangulated ShapeGeometry faces (CCW → +Z normal).
    const appendCap = (shape: THREE.Shape, z: number, flip: boolean) => {
        const cap = new THREE.ShapeGeometry(shape, segments).toNonIndexed();
        const capPositions = cap.attributes.position.array as ArrayLike<number>;
        for (let i = 0; i + 2 < capPositions.length; i += 3) {
            const x = capPositions[i];
            const y = capPositions[i + 1];
            positions.push(x, y, z);
        }
        cap.dispose();
        if (flip) {
            // Reverse the just-appended triangles (bottom cap faces -Z).
            const triCount = Math.floor((positions.length - capPositions.length) / 9);
            const base = positions.length - triCount * 9;
            for (let t = 0; t < triCount; t++) {
                const o = base + t * 9;
                const x1 = positions[o + 3], y1 = positions[o + 4], z1 = positions[o + 5];
                positions[o + 3] = positions[o + 6]; positions[o + 4] = positions[o + 7]; positions[o + 5] = positions[o + 8];
                positions[o + 6] = x1; positions[o + 7] = y1; positions[o + 8] = z1;
            }
        }
    };
    appendCap(bottom, 0, true);
    appendCap(top, height, false);

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.computeVertexNormals();
    return geometry;
}

/**
 * Swept blend: a profile morphing from `start` to `end` along `path`.
 * Builds frames along the curve (Frenet), places the arc-length resampled
 * profile (linearly interpolated start→end) perpendicular to the tangent at
 * every step and skins the rings into a closed solid with end caps.
 */
export function createSweptBlendGeometry(
    start: THREE.Shape,
    end: THREE.Shape,
    path: THREE.CurvePath<THREE.Vector3>,
    steps = 32,
    profileSegments = 24
): THREE.BufferGeometry {
    const startLoop = resampleClosed(start, profileSegments);
    const endLoop = alignLoopStart(resampleClosed(end, profileSegments), startLoop[0]);
    const n = Math.min(startLoop.length, endLoop.length);

    const frames = path.computeFrenetFrames(steps, false);
    const positions: number[] = [];

    const ringAt = (stepIndex: number): THREE.Vector3[] => {
        const t = stepIndex / steps;
        const centre = path.getPointAt(t);
        const normal = frames.normals[Math.min(stepIndex, frames.normals.length - 1)];
        const binormal = frames.binormals[Math.min(stepIndex, frames.binormals.length - 1)];
        const ring: THREE.Vector3[] = [];
        for (let i = 0; i < n; i++) {
            const s = startLoop[i];
            const e = endLoop[i];
            const x = s.x + (e.x - s.x) * t;
            const y = s.y + (e.y - s.y) * t;
            ring.push(
                new THREE.Vector3()
                    .addScaledVector(normal, x)
                    .addScaledVector(binormal, y)
                    .add(centre)
            );
        }
        return ring;
    };

    let previous = ringAt(0);
    for (let s = 1; s <= steps; s++) {
        const current = ringAt(s);
        for (let i = 0; i < n; i++) {
            const iNext = (i + 1) % n;
            const p0 = previous[i], p1 = previous[iNext], c1 = current[iNext], c0 = current[i];
            positions.push(p0.x, p0.y, p0.z, p1.x, p1.y, p1.z, c1.x, c1.y, c1.z);
            positions.push(p0.x, p0.y, p0.z, c1.x, c1.y, c1.z, c0.x, c0.y, c0.z);
        }
        previous = current;
    }

    // End caps: flat triangulated faces oriented with the path tangent.
    const capTriangles = (loop: THREE.Vector2[], centre: THREE.Vector3, normal: THREE.Vector3, binormal: THREE.Vector3, flip: boolean) => {
        const toWorld = (p: THREE.Vector2) =>
            new THREE.Vector3().addScaledVector(normal, p.x).addScaledVector(binormal, p.y).add(centre);
        const c0 = centre.clone();
        for (let i = 0; i < n; i++) {
            const a = toWorld(loop[i]);
            const b = toWorld(loop[(i + 1) % n]);
            if (flip) {
                positions.push(c0.x, c0.y, c0.z, b.x, b.y, b.z, a.x, a.y, a.z);
            } else {
                positions.push(c0.x, c0.y, c0.z, a.x, a.y, a.z, b.x, b.y, b.z);
            }
        }
    };
    capTriangles(startLoop, path.getPointAt(0), frames.normals[0], frames.binormals[0], true);
    const last = frames.normals.length - 1;
    capTriangles(endLoop, path.getPointAt(1), frames.normals[last], frames.binormals[last], false);

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.computeVertexNormals();
    return geometry;
}
